import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
    ArrowLeft, Phone, Mail, MapPin, MessageCircle, Calendar, Plus, Pencil, Trash2,
    PhoneCall, FileText, Send, Eye, Save, AlertTriangle,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import CRMContactDialog from "../components/CRMContactDialog";
import { useAuth } from "../contexts/AuthContext";
import { stageLabel, STAGE_COLORS } from "../lib/crmStages";

const STAGES = ["new", "contacted", "interested", "ordering", "inactive", "lost"];
const ACTIVITY_ICONS = {
    call: PhoneCall, whatsapp: MessageCircle, visit: MapPin, email: Mail,
    note: FileText, "order-followup": Send, demo: Eye, other: FileText,
};
const ACTIVITY_TYPES = ["call", "whatsapp", "visit", "email", "note", "order-followup", "demo", "other"];

export default function CRMContactDetail({ basePath = "/admin/crm" }) {
    const { id } = useParams();
    const { user } = useAuth();
    const nav = useNavigate();
    const [contact, setContact] = useState(null);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const [activityOpen, setActivityOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const canManage = user?.role === "admin" || user?.role === "editor";

    const load = async () => {
        setLoading(true);
        try {
            const [c, a] = await Promise.all([
                api.get(`/crm/contacts/${id}`),
                api.get(`/crm/contacts/${id}/activities`),
            ]);
            setContact(c.data);
            setActivities(a.data);
        } catch (e) {
            if (e.response?.status === 404) toast.error("Contact not found");
            else toast.error(e.response?.data?.detail || "Couldn't load contact");
            setTimeout(() => nav(basePath), 1500);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const changeStage = async (newStage) => {
        if (newStage === contact.stage) return;
        try {
            await api.put(`/crm/contacts/${id}/stage`, { stage: newStage });
            toast.success(`Stage: ${contact.stage} → ${newStage}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't change stage");
        }
    };

    const doDelete = async () => {
        setBusy(true);
        try {
            await api.delete(`/crm/contacts/${id}`);
            toast.success(`'${contact.name}' deleted`);
            nav(basePath);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't delete");
            setBusy(false);
        }
    };

    const removeActivity = async (act) => {
        if (!window.confirm(`Delete activity "${act.summary.slice(0, 50)}"?`)) return;
        try {
            await api.delete(`/crm/contacts/${id}/activities/${act.id}`);
            toast.success("Activity removed");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't delete activity");
        }
    };

    if (loading) return <div className="p-12 text-stone-500">Loading…</div>;
    if (!contact) return null;

    const fu = contact.next_follow_up_at;
    const today = new Date().toISOString().slice(0, 10);
    const overdue = fu && fu < today;
    const due = fu === today;

    return (
        <div data-testid="crm-detail-page">
            <Toaster position="top-center" />

            <Link to={basePath} className="text-xs uppercase tracking-widest text-sky-700 hover:underline inline-flex items-center mb-4">
                <ArrowLeft className="w-3 h-3 mr-1" />Back to CRM
            </Link>

            <div className="bg-white border border-sky-100 p-6 mb-4">
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <div className="label-eyebrow text-sky-700">{contact.contact_type}</div>
                        <h1 className="font-serif text-3xl text-slate-900 mt-1">{contact.name}</h1>
                        {contact.company && <div className="text-stone-600 mt-1">{contact.company}</div>}
                        <div className="flex flex-wrap gap-4 mt-3 text-sm text-stone-700">
                            {contact.phone && <a href={`tel:${contact.phone}`} className="inline-flex items-center hover:text-sky-700"><Phone className="w-3.5 h-3.5 mr-1" />{contact.phone}</a>}
                            {contact.whatsapp && <a href={`https://wa.me/${contact.whatsapp.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center text-emerald-700 hover:text-emerald-800"><MessageCircle className="w-3.5 h-3.5 mr-1" />WhatsApp</a>}
                            {contact.email && <a href={`mailto:${contact.email}`} className="inline-flex items-center hover:text-sky-700"><Mail className="w-3.5 h-3.5 mr-1" />{contact.email}</a>}
                            {[contact.city, contact.state].filter(Boolean).length > 0 && (
                                <span className="inline-flex items-center"><MapPin className="w-3.5 h-3.5 mr-1 text-stone-500" />{[contact.city, contact.state].filter(Boolean).join(", ")}</span>
                            )}
                        </div>
                        {contact.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-3" data-testid="contact-tags">
                                {contact.tags.map((t) => <span key={t} className="bg-sky-100 text-sky-800 px-2 py-1 text-[11px]">{t}</span>)}
                            </div>
                        )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <Button onClick={() => setActivityOpen(true)} className="rounded-none bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-activity">
                            <Plus className="w-4 h-4 mr-1" />Log activity
                        </Button>
                        <Button onClick={() => setEditOpen(true)} variant="outline" className="rounded-none border-stone-400" data-testid="edit-contact">
                            <Pencil className="w-4 h-4 mr-1" />Edit
                        </Button>
                        {canManage && (
                            <Button onClick={() => setDeleteOpen(true)} variant="outline" className="rounded-none border-red-400 text-red-700 hover:bg-red-50" data-testid="delete-contact">
                                <Trash2 className="w-4 h-4 mr-1" />Delete
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                {/* Pipeline + meta column */}
                <aside className="space-y-3">
                    <div className="bg-white border border-sky-100 p-4">
                        <div className="text-[10px] uppercase tracking-widest text-stone-500">Pipeline stage</div>
                        <Select value={contact.stage} onValueChange={changeStage}>
                            <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="stage-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{stageLabel(contact.channel, s)}</SelectItem>)}</SelectContent>
                        </Select>
                        <div className="mt-2">
                            <Badge className={`rounded-none ${STAGE_COLORS[contact.stage]}`}>{stageLabel(contact.channel, contact.stage)}</Badge>
                        </div>
                    </div>

                    <div className="bg-white border border-sky-100 p-4 space-y-2 text-sm">
                        <Row label="Assigned MR" value={contact.assigned_mr_name || "—"} sub={contact.assigned_mr_territory} />
                        <Row label="Est. monthly Rs" value={contact.estimated_value ? formatINR(contact.estimated_value) : "—"} cls="text-sky-700 font-mono" />
                        <Row
                            label="Next follow-up"
                            value={fu || "—"}
                            cls={overdue ? "text-red-700 font-semibold" : due ? "text-amber-700 font-semibold" : ""}
                            icon={overdue ? <AlertTriangle className="w-3 h-3" /> : due ? <Calendar className="w-3 h-3" /> : null}
                        />
                        <Row label="Source" value={contact.source || "—"} />
                        <Row label="Created" value={contact.created_at?.slice(0, 10)} sub={contact.created_by_email} />
                    </div>

                    {contact.notes && (
                        <div className="bg-amber-50 border border-amber-200 p-4">
                            <div className="text-[10px] uppercase tracking-widest text-amber-800">Pinned note</div>
                            <p className="text-sm text-amber-900 mt-1 whitespace-pre-wrap">{contact.notes}</p>
                        </div>
                    )}
                </aside>

                {/* Activity timeline */}
                <div className="lg:col-span-2 bg-white border border-sky-100">
                    <div className="p-4 border-b border-sky-100 flex items-center justify-between">
                        <div>
                            <div className="label-eyebrow text-sky-700">Timeline</div>
                            <div className="font-serif text-lg text-slate-900 mt-1">{activities.length} activit{activities.length === 1 ? "y" : "ies"}</div>
                        </div>
                        <Button onClick={() => setActivityOpen(true)} variant="outline" className="rounded-none border-sky-700 text-sky-700" data-testid="add-activity-2">
                            <Plus className="w-3.5 h-3.5 mr-1" />Log activity
                        </Button>
                    </div>
                    {activities.length === 0 ? (
                        <div className="p-12 text-center text-stone-500">
                            <p>No activities yet.</p>
                            <p className="text-xs mt-2">Click "Log activity" to record a call, visit, or WhatsApp message.</p>
                        </div>
                    ) : (
                        <ol className="divide-y divide-stone-100" data-testid="timeline">
                            {activities.map((a) => {
                                const Icon = ACTIVITY_ICONS[a.activity_type] || FileText;
                                return (
                                    <li key={a.id} className="p-4 flex gap-3" data-testid={`activity-${a.id}`}>
                                        <div className="w-9 h-9 grid place-items-center bg-sky-50 text-sky-700 flex-shrink-0">
                                            <Icon className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-baseline justify-between flex-wrap gap-1">
                                                <div>
                                                    <span className="text-[10px] uppercase tracking-widest text-sky-700">{a.activity_type}</span>
                                                    {a.outcome && <span className="ml-2 text-[10px] uppercase tracking-widest text-stone-500">· {a.outcome}</span>}
                                                </div>
                                                <div className="text-[11px] text-stone-500">
                                                    {a.happened_at?.slice(0, 16).replace("T", " ")} · {a.actor_email}
                                                </div>
                                            </div>
                                            <p className="text-sm text-slate-900 mt-1 whitespace-pre-wrap">{a.summary}</p>
                                            {a.next_action && (
                                                <div className="mt-2 text-xs bg-amber-50 border-l-2 border-amber-400 px-2 py-1 text-amber-900">
                                                    <strong>Next:</strong> {a.next_action}
                                                    {a.next_action_at && <span className="ml-1">· {a.next_action_at.slice(0, 10)}</span>}
                                                </div>
                                            )}
                                        </div>
                                        <button onClick={() => removeActivity(a)} className="text-stone-400 hover:text-red-700 self-start" data-testid={`activity-delete-${a.id}`} title="Delete activity">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </div>
            </div>

            {/* Dialogs */}
            <CRMContactDialog open={editOpen} onOpenChange={setEditOpen} contact={contact} onSaved={() => load()} />
            <ActivityDialog open={activityOpen} onOpenChange={setActivityOpen} contactId={id} onSaved={() => load()} />

            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete '{contact.name}'?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Removes the contact and all {activities.length} timeline entries.
                            <span className="block mt-2 text-xs">This action is logged for admins.</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} disabled={busy} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="confirm-delete">
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function ActivityDialog({ open, onOpenChange, contactId, onSaved }) {
    const [form, setForm] = useState({ activity_type: "call", summary: "", outcome: "", next_action: "", next_action_at: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) setForm({ activity_type: "call", summary: "", outcome: "", next_action: "", next_action_at: "" });
    }, [open]);

    const submit = async () => {
        if (!form.summary.trim()) return toast.error("Summary is required");
        setBusy(true);
        try {
            await api.post(`/crm/contacts/${contactId}/activities`, {
                ...form,
                next_action_at: form.next_action_at || null,
            });
            toast.success("Activity logged");
            onOpenChange(false);
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't log activity");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-none max-w-lg">
                <DialogHeader>
                    <DialogTitle>Log activity</DialogTitle>
                    <DialogDescription>Record a call, visit, WhatsApp message or note about this contact.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-stone-500">Type</Label>
                        <Select value={form.activity_type} onValueChange={(v) => setForm((f) => ({ ...f, activity_type: v }))}>
                            <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="act-type"><SelectValue /></SelectTrigger>
                            <SelectContent>{ACTIVITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-stone-500">What happened? *</Label>
                        <Textarea rows={3} value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))} className="rounded-none border-stone-300 mt-1" placeholder="e.g. Discussed Argentum Nit dosage, requested samples" data-testid="act-summary" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Outcome</Label>
                            <Input value={form.outcome} onChange={(e) => setForm((f) => ({ ...f, outcome: e.target.value }))} className="rounded-none border-stone-300 mt-1" placeholder="interested / busy / not-reachable" data-testid="act-outcome" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Next follow-up date</Label>
                            <Input type="date" value={form.next_action_at} onChange={(e) => setForm((f) => ({ ...f, next_action_at: e.target.value }))} className="rounded-none border-stone-300 mt-1" data-testid="act-next-date" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-stone-500">Next action</Label>
                        <Input value={form.next_action} onChange={(e) => setForm((f) => ({ ...f, next_action: e.target.value }))} className="rounded-none border-stone-300 mt-1" placeholder="e.g. Send sample, call again Tuesday" data-testid="act-next-action" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none" data-testid="act-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white" data-testid="act-save">
                        <Save className="w-4 h-4 mr-2" />{busy ? "Saving…" : "Log activity"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

const Row = ({ label, value, sub, cls = "", icon }) => (
    <div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1">{icon}{label}</div>
        <div className={`mt-0.5 ${cls}`}>{value}</div>
        {sub && <div className="text-[11px] text-stone-500">{sub}</div>}
    </div>
);
